import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  HostBinding,
  inject, Input,
  signal,
  WritableSignal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { CountryVoModel } from '@well/common-libs/country-management/domain-model';
import { CountryStoreFacade } from '@well/common-libs/country-management/state';
import { FloatLabel } from 'primeng/floatlabel';
import {
  BsAutocompleteComponent,
  BsItemDirective,
  BsFloatLabelCompatDirective
} from 'iriro-frontend-libs/bs-component-store';

@Component({
  selector: 'well-country-dropdown',
  host: { class: 'irian-custom-cva' },
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    FloatLabel,
    BsAutocompleteComponent,
    BsItemDirective,
    BsFloatLabelCompatDirective
  ],
  templateUrl: './country-dropdown.component.html',
  styleUrls: ['./country-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountryDropdownComponent),
      multi: true,
    }
  ],
})
export class CountryDropdownComponent implements ControlValueAccessor {
  private _onChange: any | undefined = undefined;
  private _onTouched: any | undefined = undefined;
  disabled = false;
  @Input() readonly = false;
  @Input() label: string | undefined = undefined;
  @Input() floatLabelVariant: 'in' | 'over' | 'on' = 'on';

  private query: WritableSignal<any> = signal({ value: '' });

  @HostBinding('class.p-inputwrapper-focus') focus = false;
  @HostBinding('class.p-inputwrapper-filled') filled = false;

  private readonly countryStoreFacade = inject(CountryStoreFacade);

  countries = toSignal(this.countryStoreFacade.countries$);

  result = computed(() => {
    return this.countries()?.filter((item: any) => item.name?.toLowerCase()?.includes(this.query().value.toLowerCase()));
  });

  selectedCode: WritableSignal<string | undefined | null> = signal('');
  externalCode: WritableSignal<string | undefined | null> = signal('');

  selectedResource = computed(() => {
    return this.countries()?.find((item: any) => item.code === this.externalCode());
  });

  registerOnChange(fn: any): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(code: string): void {
    if (code && code !== this.selectedCode()) {
      this.externalCode?.set(code);
      this.selectedCode?.set(code);
      this.filled = true;
    }
    if (code === null) {
      this.externalCode?.set(null);
      this.selectedCode?.set(null);
      this.filled = false;
    }
  }

  onQueryChange(query: string) {
    this.query.set({ value: query, data: Date.now() });
  }

  onSelect(event: CountryVoModel) {
    this.selectedCode?.set(event?.code);
    this._onChange(this.selectedCode());
    this._onTouched();
    this.filled = !!this.selectedCode();
  }

  onBlur() {
    this.focus = false;
    if (this._onTouched) {
      this._onTouched();
    }
  }

  onInputFocus() {
    this.focus = true;
    if (this._onTouched) {
      this._onTouched();
    }
  }

  getDisplayName = (item: CountryVoModel | null): string => {
    return item?.name ?? '';
  };

  compareByCode = (a: CountryVoModel, b: CountryVoModel): boolean => {
    return a?.code === b?.code;
  };
}
